import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';

import * as Tooltip from '@radix-ui/react-tooltip';

import {
    changeTheme,
    updateScaleMinus,
    updateScalePlus,
    resetScale,
    toggleBtnMinimap,
    toggleBtnInfoResources,
    toggleBtnModalShortcuts,
} from '../../../redux/diagram/diagram.actions';

import { toggleSupport, toggleModalLanguages } from '../../../redux/main/main.actions';


import {
    selectThemeMode,
    selectFocusedInsideElement,
    selectIsBtnMinimapEnabled,
    selectIsBtnInfoResourcesEnabled,
    selectScale,
    selectOriginXY,
    selectIsEnabledElementDescriptionEditing,

    selectIsBtnSearchEnabled,
    selectIsBtnExportImageEnabled,

    selectIsBtnModalShortcutsEnabled,
} from '../../../redux/diagram/diagram.selectors';

import {
    selectIsModalSupportEnabled,
    selectIsModalDiagramDetailsEnabled,
    selectIsModalDiagramShareEnabled,
} from '../../../redux/main/main.selectors';

// import { ModeDarkIcon, ModeLightIcon, ResizeToFitIcon, FullScreenIcon, PlusIcon, MinusIcon } from "../Icon";
// eslint-disable-next-line
import {
    InfoQuestionIcon,
    MiniMapIcon,
    ModeDarkIcon,
    ModeLightIcon,
    FullScreenIcon,
    FullScreenCloseIcon,
    PlusIcon,
    MinusIcon,
} from '../../Icon';

import { useShortKeysNavigation } from '../../../hooks/useShortKeysNavigation';
import { useDeviceType } from '../../../hooks/useDeviceType.ts';

import './ControlsBottom.scss';

const INITIAL_SCALE_VALUE = 0.9;

export const ControlsBottom = ({ canvasRef, minimapRef }) => {
    const { map: shortKeysMap, isMac } = useShortKeysNavigation();
    const { isDesktop } = useDeviceType(); // mobile/tablet => false

    const { t, i18n } = useTranslation();

    const scale = useSelector(selectScale);
    const originXY = useSelector(selectOriginXY);

    // board origin {x, y} - offsets when you drag & move board | = (e.clientX)
    const originXYRef = useRef({ x: 0, y: 300 });
    // board scale
    const scaleRef = useRef(scale);
    const [scaleState, setScaleState] = useState(scale);
    // const [isChangedState, setIsChangedState] = useState(false);

    const dispatch = useDispatch();
    const themeMode = useSelector(selectThemeMode);


    const isModalSupportEnabled = useSelector(selectIsModalSupportEnabled);
    const isModalDiagramDetailsEnabled = useSelector(selectIsModalDiagramDetailsEnabled);
    const isModalDiagramShareEnabled = useSelector(selectIsModalDiagramShareEnabled);

    const isBtnMinimapEnabled = useSelector(selectIsBtnMinimapEnabled);

    const isBtnInfoResourcesEnabled = useSelector(selectIsBtnInfoResourcesEnabled);
    const infoResourcesBtnRef = useRef(null);
    const infoResourcesMenuRef = useRef(null);

    const [isFullScreen, setIsFullScreen] = useState(false);

    const focusedInsideElementRedux = useSelector(selectFocusedInsideElement);

    const isEnabledElementDescriptionEditing = useSelector(selectIsEnabledElementDescriptionEditing);

    const isBtnSearchEnabled = useSelector(selectIsBtnSearchEnabled);
    const isBtnExportImageEnabled = useSelector(selectIsBtnExportImageEnabled);

    const isBtnModalShortcutsEnabled = useSelector(selectIsBtnModalShortcutsEnabled);


    // function toggleFullScreen() {
    //     const panelElement = document.getElementsByClassName('panel-panel')[0];
    //     if (
    //         !document.fullscreenElement && // alternative standard method
    //         !document.mozFullScreenElement &&
    //         !document.webkitFullscreenElement &&
    //         !document.msFullscreenElement
    //     ) {
    //         // current working methods

    //         setIsFullScreen(true);

    //         if (panelElement.requestFullscreen) {
    //             panelElement.requestFullscreen();
    //         } else if (panelElement.msRequestFullscreen) {
    //             panelElement.msRequestFullscreen();
    //         } else if (panelElement.mozRequestFullScreen) {
    //             panelElement.mozRequestFullScreen();
    //         } else if (panelElement.webkitRequestFullscreen) {
    //             panelElement.webkitRequestFullscreen(
    //                 Element.ALLOW_KEYBOARD_INPUT
    //             );
    //         }
    //     } else {
    //         setIsFullScreen(false);

    //         if (document.exitFullscreen) {
    //             document.exitFullscreen();
    //         } else if (document.msExitFullscreen) {
    //             document.msExitFullscreen();
    //         } else if (document.mozCancelFullScreen) {
    //             document.mozCancelFullScreen();
    //         } else if (document.webkitExitFullscreen) {
    //             document.webkitExitFullscreen();
    //         }
    //     }
    // }


    function toggleFullScreen() {
        if (!isFullScreen) {
            enterFullscreen();
        } else {
            exitFullscreen();
        }
    }

    const enterFullscreen = () => {
        if (document.documentElement.requestFullscreen) {
            document.documentElement.requestFullscreen();
        } else if (document.documentElement.mozRequestFullScreen) { // Firefox
            document.documentElement.mozRequestFullScreen();
        } else if (document.documentElement.webkitRequestFullscreen) { // Chrome, Safari, Opera
            document.documentElement.webkitRequestFullscreen();
        } else if (document.documentElement.msRequestFullscreen) { // IE/Edge
            document.documentElement.msRequestFullscreen();
        }
    };

    // Функція для виходу з повного екрану
    const exitFullscreen = () => {
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.mozCancelFullScreen) { // Firefox
            document.mozCancelFullScreen();
        } else if (document.webkitExitFullscreen) { // Chrome, Safari, Opera
            document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) { // IE/Edge
            document.msExitFullscreen();
        }
    };

    // Слухач події для зміни стану повного екрану
    useEffect(() => {
        const onFullscreenChange = () => {
            setIsFullScreen(!!document.fullscreenElement);
        };

        // Додаємо подію для змін стану повного екрану
        document.addEventListener('fullscreenchange', onFullscreenChange);
        document.addEventListener('webkitfullscreenchange', onFullscreenChange);
        document.addEventListener('mozfullscreenchange', onFullscreenChange);
        document.addEventListener('MSFullscreenChange', onFullscreenChange);

        // Очистка при анмаунті компонента
        return () => {
            document.removeEventListener('fullscreenchange', onFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
            document.removeEventListener('mozfullscreenchange', onFullscreenChange);
            document.removeEventListener('MSFullscreenChange', onFullscreenChange);
        };
    }, []);



    useEffect(() => {
        const handleCanvasWheel = (event) => {
            event.preventDefault();

            const mouseX = event.clientX;
            const mouseY = event.clientY;

            // // previous zoom
            // const wheel = event.deltaY < 0 ? 1 : -1;
            // const ZOOM_FACTOR = 0.15;
            // const zoom = Math.exp(wheel * ZOOM_FACTOR);
            // const newScale = scale * zoom;

            let delta = Math.max(-600, Math.min(600, event.deltaY));

            // const zoomIntensity = 0.001;
            let zoomIntensity = 0.001;

            if (scaleRef.current <= 1.1 && scaleRef.current > 0.65) {
                zoomIntensity = 0.001;
                delta = Math.max(-500, Math.min(500, event.deltaY));
            } else if (scaleRef.current <= 0.65 && scaleRef.current > 0.12) {
                // zoom in
                if (event.deltaY < 0) {
                    delta = Math.max(-600, Math.min(600, event.deltaY));
                    zoomIntensity = 0.0012;

                    // zoom out
                } else {
                    delta = Math.max(-450, Math.min(450, event.deltaY));
                    zoomIntensity = 0.0012;
                }
            } else if (scaleRef.current <= 0.12) {
                // zoom in
                if (event.deltaY < 0) {
                    delta = Math.max(-350, Math.min(350, event.deltaY));
                    zoomIntensity = 0.0025;

                    // zoom out
                } else {
                    delta = Math.max(-350, Math.min(350, event.deltaY));
                    zoomIntensity = 0.0015;
                }
            }

            const newScale = scaleRef.current * (1 - delta * zoomIntensity);
            // const newScale = scaleRef.current + event.deltaY * -0.001; // COEF should be dinamic changed on edge values(on min and max values) 			// const COEF = -0.002 or -0.0025 or -0.001;

            const clampedScale = Math.min(Math.max(newScale, 0.045), 4.05);

            const zoomFactor = clampedScale / scaleRef.current;

            const newOriginX =
                (originXYRef.current.x - mouseX) * zoomFactor + mouseX;
            const newOriginY =
                (originXYRef.current.y - mouseY) * zoomFactor + mouseY;

            scaleRef.current = clampedScale;
            // setScaleState(clampedScale);
            // setIsChangedState(!isChangedState);

            originXYRef.current = { x: newOriginX, y: newOriginY };
            // calculateAndSetScaleNumbers(clampedScale);

            // if (!isScaling) { setIsScaling(true); }
            // clearTimeout(scalingTimerRef.current);
            // scalingTimerRef.current = setTimeout(() => { setIsScaling(false); }, 200);

            // calculateVisibleElementsAndLines();
        };

        const handleMinimapWheel = (event) => {
            event.preventDefault();

            const mouseX = event.clientX;
            const mouseY = event.clientY;

            // // previous zoom
            // const wheel = event.deltaY < 0 ? 1 : -1;
            // const ZOOM_FACTOR = 0.15;
            // const zoom = Math.exp(wheel * ZOOM_FACTOR);
            // const newScale = scale * zoom;

            let delta = Math.max(-600, Math.min(600, event.deltaY));

            // const zoomIntensity = 0.001;
            let zoomIntensity = 0.001;

            if (scaleRef.current <= 1.1 && scaleRef.current > 0.65) {
                zoomIntensity = 0.001;
                delta = Math.max(-500, Math.min(500, event.deltaY));
            } else if (scaleRef.current <= 0.65 && scaleRef.current > 0.12) {
                // zoom in
                if (event.deltaY < 0) {
                    delta = Math.max(-600, Math.min(600, event.deltaY));
                    zoomIntensity = 0.0012;

                    // zoom out
                } else {
                    delta = Math.max(-450, Math.min(450, event.deltaY));
                    zoomIntensity = 0.0012;
                }
            } else if (scaleRef.current <= 0.12) {
                // zoom in
                if (event.deltaY < 0) {
                    delta = Math.max(-350, Math.min(350, event.deltaY));
                    zoomIntensity = 0.0025;

                    // zoom out
                } else {
                    delta = Math.max(-350, Math.min(350, event.deltaY));
                    zoomIntensity = 0.0015;
                }
            }

            const newScale = scaleRef.current * (1 - delta * zoomIntensity);
            // const newScale = scaleRef.current + event.deltaY * -0.001; // COEF should be dinamic changed on edge values(on min and max values) 			// const COEF = -0.002 or -0.0025 or -0.001;

            const clampedScale = Math.min(Math.max(newScale, 0.045), 4.05);

            const zoomFactor = clampedScale / scaleRef.current;

            const newOriginX =
                (originXYRef.current.x - mouseX) * zoomFactor + mouseX;
            const newOriginY =
                (originXYRef.current.y - mouseY) * zoomFactor + mouseY;

            scaleRef.current = clampedScale;
            // setScaleState(clampedScale);
            // setIsChangedState(!isChangedState);

            originXYRef.current = { x: newOriginX, y: newOriginY };
            // calculateAndSetScaleNumbers(clampedScale);

            // if (!isScaling) { setIsScaling(true); }
            // clearTimeout(scalingTimerRef.current);
            // scalingTimerRef.current = setTimeout(() => { setIsScaling(false); }, 200);

            // calculateVisibleElementsAndLines();
        };

        if (canvasRef.current) {
            canvasRef.current.addEventListener('wheel', handleCanvasWheel);
        }

        if (minimapRef.current && minimapRef.current.dynamicCanvas) {
            minimapRef.current.dynamicCanvas.addEventListener(
                'wheel',
                handleMinimapWheel
            );
        }

        return () => {
            if (canvasRef.current) {
                canvasRef.current.removeEventListener(
                    'wheel',
                    handleCanvasWheel
                );
            }
            if (minimapRef.current && minimapRef.current.dynamicCanvas) {
                minimapRef.current.dynamicCanvas.removeEventListener(
                    'wheel',
                    handleMinimapWheel
                );
            }
        };
    }, [canvasRef, minimapRef]);

    useEffect(() => {
        let animationFrameId;

        const updateScale = () => {
            // Оновлюємо стан компонента
            setScaleState(scaleRef.current);
            // Запитуємо наступний кадр
            animationFrameId = requestAnimationFrame(updateScale);
        };

        // Запускаємо оновлення при монтуванні компонента
        animationFrameId = requestAnimationFrame(updateScale);

        return () => {
            // Очищаємо requestAnimationFrame при демонтажі
            cancelAnimationFrame(animationFrameId);
        };
    }, [scale]);

    const handleScaleChange = (scaleValue) => {
        if (scaleValue !== scaleRef.current) {
            scaleRef.current = scaleValue;
            // setScaleState(scaleValue);

            // calculateAndSetScaleNumbers(scaleValue);

            // setIsScaling(true);
            // clearTimeout(scalingTimerRef.current);
            // scalingTimerRef.current = setTimeout(() => { setIsScaling(false); }, 200);

            // draw();
        }
    };
    useEffect(() => {
        handleScaleChange(scale);
        // draw();
    }, [scale]);

    const handleOriginXYChange = (originXYValue) => {
        if (
            originXYValue.x !== originXYRef.current.x ||
            originXYValue.y !== originXYRef.current.y
        ) {
            originXYRef.current = { x: originXYValue.x, y: originXYValue.y };
            // draw();
        }
    };
    useEffect(() => {
        handleOriginXYChange(originXY);
        // draw();
    }, [originXY]);

    useEffect(() => {
        const handleKeyPress = (event) => {
            const isCtrl = isMac ? event.metaKey : event.ctrlKey;

            const isOpenTextEditing = focusedInsideElementRedux || isEnabledElementDescriptionEditing;

            // Ignore events from disabled or read-only elements
            if (event.target.disabled || event.target.readOnly) return;

            // 0. IF MODALS OPEN, ALLOW NOTHING
            // DISABLE BASIC MOVE & NO MINIMAP
            if (
                isModalDiagramDetailsEnabled ||
                isModalSupportEnabled ||
                isModalDiagramShareEnabled ||
                isBtnSearchEnabled
            ) {
                // ZOOM OUT
                // [CTRL -]
                if (
                    !event.altKey &&
                    (event.code === 'Minus' || event.code === 'NumpadSubtract')
                ) {
                    // [CTRL -]
                    if (isCtrl) {
                        event.stopPropagation();
                        event.preventDefault();
                    }
                }
                // ZOOM IN
                // [CTRL +]
                else if (
                    !event.altKey &&
                    (event.code === 'Equal' || event.code === 'NumpadAdd')
                ) {
                    // [CTRL +]
                    if (isCtrl) {
                        event.stopPropagation();
                        event.preventDefault();
                    }
                }
                // ZOOM RESET
                // [CTRL + 0]
                else if (
                    (event.code === 'Digit0' || event.code === 'Numpad0') &&
                    isCtrl &&
                    !event.altKey &&
                    !event.shiftKey
                ) {
                    event.stopPropagation();
                    event.preventDefault();
                }

                // 1. ONLY CTRL - / CTRL + / CTRL 0
                // WHEN OPENED TEXT EDITING FIELDS
            } else if (
                isOpenTextEditing ||
                isBtnExportImageEnabled
            ) {
                // ZOOM OUT
                // [CTRL -]
                if (
                    !event.altKey &&
                    (event.code === 'Minus' || event.code === 'NumpadSubtract')
                ) {
                    // [CTRL -]
                    if (isCtrl) {
                        event.stopPropagation();
                        event.preventDefault();
                        dispatch(updateScaleMinus());
                    }
                }
                // ZOOM IN
                // [CTRL +]
                else if (
                    !event.altKey &&
                    (event.code === 'Equal' || event.code === 'NumpadAdd')
                ) {
                    // [CTRL +]
                    if (isCtrl) {
                        event.stopPropagation();
                        event.preventDefault();
                        dispatch(updateScalePlus());
                    }
                }
                // ZOOM RESET
                // [CTRL + 0]
                else if (
                    (event.code === 'Digit0' || event.code === 'Numpad0') &&
                    (isCtrl) &&
                    !event.altKey &&
                    !event.shiftKey
                ) {
                    event.stopPropagation();
                    event.preventDefault();
                    dispatch(resetScale());
                }

                // 2. ALL EVENTS
            } else {
                // ZOOM OUT
                // [-] [CTRL -]
                if (
                    !event.altKey &&
                    (event.code === 'Minus' || event.code === 'NumpadSubtract')
                ) {
                    // [CTRL -]
                    if (isCtrl) {
                        event.stopPropagation();
                        event.preventDefault();
                        dispatch(updateScaleMinus());

                        // [-]
                    } else if (!event.shiftKey) {
                        event.stopPropagation();
                        event.preventDefault();
                        dispatch(updateScaleMinus());
                    }
                }
                // ZOOM IN
                // [+] and [CTRL +]
                else if (
                    !event.altKey &&
                    (event.code === 'Equal' || event.code === 'NumpadAdd')
                ) {
                    // [CTRL +]
                    if (isCtrl) {
                        event.stopPropagation();
                        event.preventDefault();
                        dispatch(updateScalePlus());

                        // [+]
                    } else {
                        event.stopPropagation();
                        event.preventDefault();
                        dispatch(updateScalePlus());
                    }
                }
                // ZOOM RESET
                // [CTRL + 0]
                else if (
                    (event.code === 'Digit0' || event.code === 'Numpad0') &&
                    (isCtrl) &&
                    !event.altKey &&
                    !event.shiftKey
                ) {
                    event.stopPropagation();
                    event.preventDefault();
                    dispatch(resetScale());
                }
                // MINIMAP
                // [ M ]
                else if (
                    event.code === 'KeyM' &&
                    !event.ctrlKey &&
                    !event.metaKey &&
                    !event.altKey &&
                    !event.shiftKey
                ) {
                    event.preventDefault();
                    dispatch(toggleBtnMinimap());
                }
            }

            // // ZOOM OUT
            // // [-] [CTRL -]
            // if (!event.altKey && (event.code === 'Minus' || event.code === 'NumpadSubtract')) {
            // 	// [CTRL -]
            // 	if (isCtrl) {
            // 		event.stopPropagation();
            // 		event.preventDefault();
            // 		dispatch(updateScaleMinus());

            // 		// [-]
            // 	} else if (!event.shiftKey) {
            // 		event.stopPropagation();
            // 		event.preventDefault();
            // 		dispatch(updateScaleMinus());
            // 	}
            // }
            // // ZOOM IN
            // // [+] and [CTRL +]
            // else if (!event.altKey && (event.code === 'Equal' || event.code === 'NumpadAdd')) {
            // 	// [CTRL +]
            // 	if (isCtrl) {
            // 		event.stopPropagation();
            // 		event.preventDefault();
            // 		dispatch(updateScalePlus());

            // 		// [+]
            // 	} else {
            // 		event.stopPropagation();
            // 		event.preventDefault();
            // 		dispatch(updateScalePlus());
            // 	}
            // }
            // // ZOOM RESET
            // // [CTRL + 0]
            // else if ((event.code === 'Digit0' || event.code === 'Numpad0') &&
            // 	(isCtrl) && !event.altKey && !event.shiftKey) {
            // 	event.stopPropagation();
            // 	event.preventDefault();
            // 	dispatch(resetScale());
            // }
            // // MINIMAP
            // // [ M ]
            // else if (event.code === 'KeyM' &&
            // 	!event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey) {
            // 	event.preventDefault();
            // 	// dispatch(toggleBtnExportImage());
            // }
        };

        document.addEventListener('keydown', handleKeyPress);
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [
        dispatch,
        focusedInsideElementRedux,
        isModalDiagramDetailsEnabled,
        isModalSupportEnabled,
        isModalDiagramShareEnabled,
        isEnabledElementDescriptionEditing,
        isBtnSearchEnabled,
        isBtnExportImageEnabled,
    ]);





    useEffect(() => {
        const handleDocumentClick = (event) => {
            // check if press any button exept 'left mouse button' then return nothing
            if (event.button !== 0) {
                return;
            }

            // check if 'info&resources menu enabled' and click outside of 'info&resources menu' and 'info&resources button'
            if (
                isBtnInfoResourcesEnabled &&
                !infoResourcesMenuRef.current?.contains(event.target) &&
                !infoResourcesBtnRef.current?.contains(event.target)
            ) {
                dispatch(toggleBtnInfoResources());
            }
        };

        document.addEventListener('click', handleDocumentClick);
        return () => {
            document.removeEventListener('click', handleDocumentClick);
        };
    }, [
        dispatch,
        isBtnInfoResourcesEnabled,
    ]);





    // info & resources menu
    const getInfoResourcesMenu = (isBtnInfoResourcesEnabled) => {
        if (!isBtnInfoResourcesEnabled) {
            return;
        }
        if (!infoResourcesBtnRef.current) {
            return;
        }
        const rect = infoResourcesBtnRef.current.getBoundingClientRect();
        // const top = rect.bottom + window.scrollY + - 500 - 1;
        // const left = rect.left + window.scrollX + 19 - 80 - 6;

        // const top = -132;
        // const left = 109;

        // const top = -179;
        const left = 109;

        // const top = -226;

        // const top = -186;
        // const top = -139;
        const top = -186;

        return (
            <div
                className={`info-resources-group`}
                ref={infoResourcesMenuRef}
                style={{ top: `${top}px`, left: `${left}px` }}
            >
                <div className="controls-group">
                    <div className="control">
                        <button
                            className={`control-button control-button-option ${!isBtnInfoResourcesEnabled ? 'selected' : ''}`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            onClick={() => {
                                dispatch(toggleSupport('feedback', 'canvas'));
                                dispatch(toggleBtnInfoResources());
                            }}
                        >
                            {t('dropdown_ctrl_info_resources.feedback')}
                        </button>
                    </div>

                    {/* <div className="control">
                        <button
                            className={`control-button control-button-option ${!isBtnInfoResourcesEnabled ? 'selected' : ''}`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            onClick={() => {
                                dispatch(toggleSupport('support', 'canvas'));
                                dispatch(toggleBtnInfoResources());
                            }}
                        >
                            Support
                        </button>
                    </div> */}

                    {/* <div className="control-divider"></div> */}

                    {/* <div className="control">
                        <button
                            className={`control-button control-button-option disabled ${isBtnModalShortcutsEnabled ? 'selected' : ''}`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                        // onClick={() => { dispatch(toggleBtnModalShortcuts()) }}
                        >
                            Onboarding
                        </button>
                    </div> */}

                    <div className="control-divider"></div>

                    <div className="control">
                        <button
                            className={`control-button control-button-option ${isBtnModalShortcutsEnabled ? 'selected' : ''}`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            onClick={() => { dispatch(toggleBtnModalShortcuts()) }}
                        >
                            {t('dropdown_ctrl_info_resources.keyboard_shortcuts')}
                        </button>
                    </div>

                    {/* <div className="control-divider"></div> */}

                    <div className="control">
                        <button
                            className={`control-button control-button-option ${isBtnModalShortcutsEnabled ? 'selected' : ''}`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            onClick={() => {
                                dispatch(toggleModalLanguages());
                                dispatch(toggleBtnInfoResources());
                            }}
                        >
                            {t('dropdown_ctrl_info_resources.languages')}
                        </button>
                    </div>

                    <div className="control-divider"></div>

                    <div className="control">
                        <button
                            className={`control-button control-button-option`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            onClick={() => {
                                setTimeout(() => { dispatch(toggleBtnInfoResources()); }, 50)
                                setTimeout(() => { window.open('https://dbdesign.io', '_blank'); }, 100)
                            }}
                        >
                            {t('dropdown_ctrl_info_resources.homepage')}
                        </button>
                    </div>

                </div>
            </div>
        );
    };

    return (
        <div className={`controls_bottom ${isBtnExportImageEnabled ? 'hide' : ''}`}>
            <div className="controls-row">
                {getInfoResourcesMenu(isBtnInfoResourcesEnabled)}

                {/* <div className="controls-group">
                    <Tooltip.Root ignoreNonKeyboardFocus  delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${
                                        isBtnInfoResourcesEnabled
                                            ? 'selected'
                                            : ''
                                    }`}
                                    type="button"
                                    // title="Title"
                                    // aria-label="Title"
                                    aria-pressed="false"
                                    onClick={() =>
                                        dispatch(toggleBtnInfoResources())
                                    }
                                >
                                    <InfoQuestionIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className={`tooltip-content ${
                                    isBtnMinimapEnabled ? 'tooltip-info' : ''
                                }`}
                                side="bottom"
                                align="center"
                                sideOffset={8}
                            >
                                <span>Information & Resources</span>

                                <Tooltip.Arrow className="tooltip-arrow" />
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div> */}

                {isDesktop ?

                    <div className="controls-group">
                        <Tooltip.Root ignoreNonKeyboardFocus delayDuration={600} disableHoverableContent>
                            <Tooltip.Trigger asChild>
                                <div className="control">
                                    <button
                                        className={`control-button ${isFullScreen && 'selected'
                                            }`}
                                        // className={`control-button`}
                                        type="button"
                                        // title="Full Screen"
                                        // aria-label="Full Screen"
                                        aria-pressed="false"
                                        onClick={() => toggleFullScreen()}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-around',
                                        }}
                                    >
                                        {isFullScreen ? (
                                            <FullScreenCloseIcon />
                                        ) : (
                                            <FullScreenIcon />
                                        )}
                                    </button>
                                </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content
                                    className="tooltip-content tooltip-controls wo-shortcut"
                                    side="bottom"
                                    align="center"
                                    sideOffset={10}
                                >
                                    <span>{t('t.ctrl_bottom_right.full_screen')}</span>

                                    {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>

                        {/* <div className="control">
						<button
							className='control-button'
							title="Reset Size to Fit / Zoom to Fit"
							aria-label="Reset Size to Fit / Zoom to Fit"
							aria-pressed="false"
							onClick={() => resetTransform()}
							style={{ paddingTop: 2, }}
							type="button"
						>
							<ResizeToFitIcon />
						</button>
					</div> */}

                        <Tooltip.Root ignoreNonKeyboardFocus delayDuration={600} disableHoverableContent>
                            <Tooltip.Trigger asChild>
                                <div className="control">
                                    <button
                                        // disabled
                                        // className='control-button disabled'
                                        className={`control-button button-logo title ${isBtnMinimapEnabled ? 'selected' : ''
                                            }`}
                                        onClick={() => dispatch(toggleBtnMinimap())}
                                        aria-pressed="false"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-around',
                                        }}
                                        type="button"
                                    >
                                        <MiniMapIcon />
                                    </button>
                                </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content
                                    className="tooltip-content tooltip-controls"
                                    side="bottom"
                                    align="center"
                                    sideOffset={10}
                                >
                                    <span>{t('t.ctrl_bottom_right.minimap')}</span>
                                    <span className="tooltip-shortcut">M</span>

                                    {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>
                    </div>
                    : ''}

                <div className="controls-group">
                    <Tooltip.Root ignoreNonKeyboardFocus delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className="control-button"
                                    type="button"
                                    // title="Zoom Out | [-] [CTRL -]"
                                    // aria-label="Zoom out"
                                    aria-disabled="false"
                                    onClick={() => {
                                        dispatch(updateScaleMinus());
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-around',
                                    }}
                                >
                                    <MinusIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctrl_bottom_right.zoom_out')}</span>
                                <span className="tooltip-shortcut">-</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root ignoreNonKeyboardFocus delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className="control-button zoom-size"
                                    type="button"
                                    // title="Zoom Reset | [CTRL + 0]"
                                    aria-label="Zoom Size"
                                    aria-disabled="false"
                                    onClick={() => {
                                        dispatch(resetScale());
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-around',
                                        fontFamily: 'Roboto, sans-serif',
                                    }}
                                >{`${Math.round((scaleState / INITIAL_SCALE_VALUE) * 100)}%`}</button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctrl_bottom_right.zoom_100')}</span>
                                <span className="tooltip-shortcut">
                                    {shortKeysMap?.CTRL} + 0
                                </span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root ignoreNonKeyboardFocus delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className="control-button"
                                    type="button"
                                    // title="Zoom In | [+] [CTRL +]"
                                    // aria-label="Zoom in"
                                    aria-disabled="false"
                                    onClick={() => {
                                        dispatch(updateScalePlus());
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-around',
                                    }}
                                >
                                    <PlusIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctrl_bottom_right.zoom_in')}</span>
                                <span className="tooltip-shortcut">+</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>

                {isDesktop ?
                    <div className="controls-group controls-group-info">
                        {/* <Tooltip.Root ignoreNonKeyboardFocus delayDuration={600} disableHoverableContent>
                            <Tooltip.Trigger asChild>
                                <div className="control">
                                    <button
                                        disabled
                                        className="control-button disabled"
                                        type="button"
                                        aria-pressed="false"
                                        onClick={(event) => {
                                            dispatch(
                                                changeTheme({ mode: event.a })
                                            );
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-around',
                                        }}
                                    >
                                        {themeMode ? (<ModeDarkIcon />) : (<ModeLightIcon />)}
                                    </button>
                                </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content
                                    className="tooltip-content tooltip-controls tooltip-theme wo-shortcut"
                                    side="bottom"
                                    align="center"
                                    sideOffset={10}
                                >
                                    <span>
                                        {themeMode ? 'Set Dark Theme (soon)' : 'Light Theme (soon)'}{' '}
                                    </span>
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root> */}


                        <Tooltip.Root ignoreNonKeyboardFocus delayDuration={600} disableHoverableContent>
                            <Tooltip.Trigger asChild>
                                <div className="control">
                                    <button
                                        ref={infoResourcesBtnRef}
                                        className={`control-button ${isBtnInfoResourcesEnabled ? 'selected' : ''}`}
                                        type="button"
                                        // title="Title"
                                        // aria-label="Title"
                                        aria-pressed="false"
                                        onClick={() =>
                                            dispatch(toggleBtnInfoResources())
                                        }
                                    >
                                        <InfoQuestionIcon />
                                    </button>
                                </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content
                                    className="tooltip-content tooltip-controls tooltip-theme wo-shortcut"
                                    // className={`tooltip-content ${isBtnMinimapEnabled ? 'tooltip-info' : ''}`}
                                    side="bottom"
                                    align="center"
                                    sideOffset={10}
                                >
                                    <span>{t('t.ctrl_bottom_right.info')}</span>
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>

                    </div>
                    : ''}
            </div>

            {/* <div className="minimap" ref={minimapRef} style={{ display: `${isBtnMinimapEnabled ? 'flex' : 'none'}` }}>
				<div className='minimap-inner'>
					<canvas className='minimap-canvas-static' ref={staticCanvasRef}></canvas>
					{Math.random() === 12 && <canvas className='minimap-canvas-1' ref={dynamicCanvasRef}></canvas>}
					<canvas className='minimap-canvas-dynamic' ref={ref}></canvas>

				</div>
			</div> */}
        </div>
    );
};
export default ControlsBottom;
